/**
 * Loading spinner component
 */
import React from 'react';

type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

type SpinnerProps = Readonly<{
  size?: SpinnerSize;
  color?: string;
  className?: string;
}>;

/**
 * Pure function to compute spinner classes
 */
const getSpinnerSize = (size: SpinnerSize): string => {
  const sizes: Record<SpinnerSize, string> = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };
  return sizes[size];
};

/**
 * Spinner component
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'text-white',
  className = '',
}) => {
  const sizeClass = getSpinnerSize(size);
  
  return (
    <div className={`${className}`}>
      <svg
        className={`animate-spin ${sizeClass} ${color}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};

/**
 * Full page loading component
 */
export const LoadingScreen: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-brand-dark">
    <Spinner size="xl" />
    <p className="mt-4 text-gray-400 text-lg">{message}</p>
  </div>
);
